import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogContentText,
  Divider,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import M from 'messages';
import s3UploadService from 'services/s3UploadService';
import fileService from 'services/fileService';
import { getMessage } from 'helpers/helper';
import classes from './styles';

const formatBytes = sizeInBytes => {
  if (!Number.isFinite(sizeInBytes) || sizeInBytes <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(sizeInBytes) / Math.log(1024)), units.length - 1);
  const value = sizeInBytes / 1024 ** unitIndex;

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const getFileKey = file => `${file.name}__${file.size}__${file.lastModified}`;

const WineListImportModal = ({ open, handleClose, handleSubmit, serviceProviderId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const getInitialFormState = () => ({
    enhancementOption: 'none',
    notes: '',
    showConfirmation: false,
    isSubmitting: false,
    selectedFiles: [],
    uploadProgress: null,
    uploadError: null,
  });

  const [enhancementOption, setEnhancementOption] = useState('none');
  const [notes, setNotes] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  // TODO: Add file size limits (per-file and total) and reject oversized files with a clear user message.
  const onDrop = useCallback(acceptedFiles => {
    setSelectedFiles(previousFiles => {
      const existingKeys = new Set(previousFiles.map(getFileKey));
      const uniqueIncomingFiles = acceptedFiles.filter(file => !existingKeys.has(getFileKey(file)));
      return [...previousFiles, ...uniqueIncomingFiles];
    });
    setUploadError(null);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    multiple: true,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
      'text/rtf': ['.rtf'],
      'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
      'application/vnd.oasis.opendocument.text': ['.odt'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif'],
      'application/octet-stream': ['.heic', '.heif'],
    },
  });

  useEffect(() => {
    const imageFiles = selectedFiles.filter(file => file.type.startsWith('image/'));
    const previews = imageFiles.map(file => ({
      key: getFileKey(file),
      name: file.name,
      url: URL.createObjectURL(file),
    }));

    setImagePreviews(previews);

    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview.url));
    };
  }, [selectedFiles]);

  const templateColumns = [
    M.get('welcome.wineImportModal.template.columns.wineName'),
    M.get('welcome.wineImportModal.template.columns.producer'),
    M.get('welcome.wineImportModal.template.columns.variety'),
    M.get('welcome.wineImportModal.template.columns.vintage'),
    M.get('welcome.wineImportModal.template.columns.country'),
    M.get('welcome.wineImportModal.template.columns.region'),
    M.get('welcome.wineImportModal.template.columns.color'),
    M.get('welcome.wineImportModal.template.columns.sweetness'),
    M.get('welcome.wineImportModal.template.columns.style'),
  ];

  const resetFormState = useCallback(() => {
    const initialState = getInitialFormState();
    setEnhancementOption(initialState.enhancementOption);
    setNotes(initialState.notes);
    setShowConfirmation(initialState.showConfirmation);
    setIsSubmitting(initialState.isSubmitting);
    setSelectedFiles(initialState.selectedFiles);
    setUploadProgress(initialState.uploadProgress);
    setUploadError(initialState.uploadError);
  }, []);

  const handleDialogClose = () => {
    handleClose();
  };

  useEffect(() => {
    if (open) {
      resetFormState();
    }
  }, [open, resetFormState]);

  const selectedFilesSize = selectedFiles.reduce((total, file) => total + file.size, 0);
  const previewSize =
    imagePreviews.length > 60 ? 28 : imagePreviews.length > 24 ? 34 : imagePreviews.length > 12 ? 40 : 44;

  const removeSelectedFile = fileToRemove => {
    const fileToRemoveKey = getFileKey(fileToRemove);
    setSelectedFiles(previousFiles => previousFiles.filter(file => getFileKey(file) !== fileToRemoveKey));
  };
  const removeSelectedFileByKey = fileKey => {
    setSelectedFiles(previousFiles => previousFiles.filter(file => getFileKey(file) !== fileKey));
  };

  const triggerTemplateDownload = useCallback(template => {
    if (!template?.url) {
      return;
    }
    const link = document.createElement('a');
    link.href = template.url;
    link.download = template.name || 'Wine_import_template.xlsx';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, []);

  const handleTemplateDownload = useCallback(async () => {
    if (templateInfo?.url) {
      triggerTemplateDownload(templateInfo);
      return;
    }

    setTemplateLoading(true);
    try {
      const response = await fileService.getWineImportTemplate();
      const data = response.data?.response_data;
      if (!data?.url) {
        throw new Error('Template link is unavailable.');
      }
      setTemplateInfo(data);
      triggerTemplateDownload(data);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), {
        variant: 'error',
      });
    } finally {
      setTemplateLoading(false);
    }
  }, [enqueueSnackbar, templateInfo, triggerTemplateDownload]);

  const handleImportSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    if (selectedFiles.length === 0) {
      setUploadError('Please upload at least one file.');
      return;
    }
    if (!serviceProviderId) {
      setUploadError('Partner context required. Please sign in again.');
      return;
    }

    setUploadError(null);
    setIsSubmitting(true);
    const totalFiles = selectedFiles.length;

    try {
      const { batchId } = await s3UploadService.createBatch({ notes, enhancementOption });

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress({ current: i + 1, total: totalFiles, percent: 0, fileName: file.name });
        await s3UploadService.uploadOneFile(file, serviceProviderId, batchId, (percent, fileName) => {
          setUploadProgress(prev => (prev ? { ...prev, percent, fileName } : null));
        });
      }

      setUploadProgress(null);
      const payload = {
        batchId,
        notes,
        enhancementOption,
        filesCount: totalFiles,
        imagesCount: selectedFiles.filter(file => file.type.startsWith('image/')).length,
        totalFilesSize: selectedFilesSize,
      };
      await handleSubmit(payload);
      setShowConfirmation(true);
    } catch (err) {
      setUploadProgress(null);
      const message = err?.response?.data?.message || err?.message || 'Upload failed. Please try again.';
      setUploadError(message);
      // eslint-disable-next-line no-console
      console.error('Wine list import upload failed', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <DialogTitle
        align="center"
        sx={{
          color: showConfirmation ? '#1A5787' : '#1a1a1a',
          fontSize: 18,
        }}>
        {showConfirmation
          ? M.get('welcome.wineImportModal.postSubmission.title')
          : M.get('welcome.wineImportModal.title')}
      </DialogTitle>
      <DialogContent>
        {showConfirmation ? (
          <DialogContentText sx={classes.postSubmissionDialog}>
            <Typography>{M.get('welcome.wineImportModal.postSubmission.firstTextLine')}</Typography>
            <Typography>{M.get('welcome.wineImportModal.postSubmission.secondTextLine')}</Typography>
            <Typography>{M.get('welcome.wineImportModal.postSubmission.thirdTextLine')}</Typography>
          </DialogContentText>
        ) : (
          <DialogContentText component="div" sx={classes.container}>
            {M.get('welcome.wineImportModal.subTitle')}
            <Box sx={classes.infoBox}>
              <Icon baseClassName="material-symbols-outlined" sx={classes.giftIconColor}>
                featured_seasonal_and_gifts
              </Icon>
              <Typography sx={classes.infoBoxTitle}>{M.get('welcome.wineImportModal.infoBox.title')}</Typography>
              <Typography sx={classes.infoBoxText}>{M.get('welcome.wineImportModal.infoBox.text')}</Typography>
            </Box>

            <Box {...getRootProps()} sx={classes.dropzone}>
              <input {...getInputProps()} />
              <FileUploadOutlinedIcon sx={classes.dropzoneIcon} />

              <Typography sx={classes.dropzoneText}>
                {isDragActive ? (
                  M.get('welcome.wineImportModal.dropzone.dragActiveText')
                ) : (
                  <>
                    {M.get('welcome.wineImportModal.dropzone.uploadText')}{' '}
                    <Box
                      component="button"
                      type="button"
                      onClick={event => {
                        event.stopPropagation();
                        openFilePicker();
                      }}
                      sx={classes.browseButton}>
                      {M.get('welcome.wineImportModal.dropzone.browseLinkText')}
                    </Box>
                  </>
                )}
              </Typography>

              <Typography sx={classes.dropzoneMetaPrimary}>
                {M.get('welcome.wineImportModal.dropzone.acceptedFormatsText')}
              </Typography>
              <Typography sx={classes.dropzoneMeta}>
                {M.get('welcome.wineImportModal.dropzone.turnaroundText')}
              </Typography>
            </Box>
            {selectedFiles.length > 0 && (
              <Box sx={classes.selectedFilesCard}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography sx={classes.selectedFilesTitle}>Selected files: {selectedFiles.length}</Typography>
                  <Button size="small" onClick={() => setSelectedFiles([])} sx={classes.clearButton}>
                    {M.get('welcome.wineImportModal.selectedFiles.buttons.clear')}
                  </Button>
                </Stack>

                <Typography sx={classes.selectedFilesMeta}>
                  {M.get('welcome.wineImportModal.selectedFiles.totalSize').replace(
                    '{{size}}',
                    formatBytes(selectedFilesSize),
                  )}
                </Typography>

                {imagePreviews.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={classes.imagePreviewRow}>
                    {imagePreviews.map(preview => (
                      <Box
                        key={preview.key}
                        sx={{
                          ...classes.imagePreviewItem,
                          width: previewSize,
                          height: previewSize,
                        }}>
                        <Box component="img" src={preview.url} alt={preview.name} sx={classes.imagePreviewImage} />
                        <Box
                          component="button"
                          type="button"
                          aria-label={`Remove ${preview.name}`}
                          onClick={() => removeSelectedFileByKey(preview.key)}
                          sx={classes.imagePreviewRemoveButton}>
                          ×
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}

                <Stack spacing={0.75} sx={classes.selectedFilesList}>
                  {selectedFiles.map(file => {
                    const isUploading = uploadProgress?.fileName === file.name;
                    return (
                      <Stack
                        key={getFileKey(file)}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}>
                        <Typography sx={classes.selectedFileName}>{file.name}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={classes.selectedFileSize}>{formatBytes(file.size)}</Typography>
                          {isUploading ? (
                            <>
                              <CircularProgress size={16} sx={{ flexShrink: 0 }} />
                              <Typography variant="body2" color="text.secondary">
                                {uploadProgress.percent}%
                              </Typography>
                            </>
                          ) : (
                            <Button
                              size="small"
                              onClick={() => removeSelectedFile(file)}
                              sx={classes.removeFileButton}
                              disabled={!!uploadProgress}>
                              {M.get('welcome.wineImportModal.selectedFiles.buttons.remove')}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Box sx={classes.templateCard}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icon baseClassName="material-symbols-outlined" sx={classes.templateTitleIcon}>
                    file_save
                  </Icon>
                  <Typography sx={classes.templateTitle}>{M.get('welcome.wineImportModal.template.title')}</Typography>
                </Stack>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Icon baseClassName="material-symbols-outlined">download</Icon>}
                  sx={classes.button}
                  onClick={handleTemplateDownload}
                  disabled={templateLoading}>
                  {M.get('welcome.wineImportModal.template.buttonText')}
                </Button>
              </Stack>

              <Typography sx={classes.templateRecommendationTitle}>
                {M.get('welcome.wineImportModal.template.recommendationTitle')}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {templateColumns.map(column => (
                  <Box key={column} sx={classes.templatePill}>
                    {column}
                  </Box>
                ))}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1} sx={classes.templateHelperRow}>
                <InfoOutlinedIcon sx={classes.templateInfoIcon} />
                <Typography sx={classes.templateHelperText}>
                  {M.get('welcome.wineImportModal.template.helperText')}
                </Typography>
              </Stack>
            </Box>

            <Divider sx={classes.sectionDivider} />

            <Stack direction="row" alignItems="center" spacing={1} sx={classes.aiHeader}>
              <Icon baseClassName="material-symbols-outlined" sx={classes.aiHeaderIcon}>
                wand_stars
              </Icon>
              <Typography sx={classes.aiTitle}>
                {M.get('welcome.wineImportModal.aiEnhancements.title')}{' '}
                <Typography component="span" sx={classes.aiOptional}>
                  {M.get('welcome.wineImportModal.aiEnhancements.optionalLabel')}
                </Typography>
              </Typography>
            </Stack>

            <Typography sx={classes.aiDescription}>
              {M.get('welcome.wineImportModal.aiEnhancements.description')}
            </Typography>

            <RadioGroup value={enhancementOption} onChange={event => setEnhancementOption(event.target.value)}>
              <FormControlLabel
                value="none"
                control={<Radio sx={{ py: 0 }} />}
                sx={classes.optionControl}
                label={
                  <Box>
                    <Typography sx={classes.optionTitle}>
                      {M.get('welcome.wineImportModal.aiEnhancements.options.none.title')}
                    </Typography>
                    <Typography sx={classes.optionDescription}>
                      {M.get('welcome.wineImportModal.aiEnhancements.options.none.description')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="image_only"
                control={<Radio sx={{ py: 0 }} />}
                sx={classes.optionControl}
                label={
                  <Box>
                    <Typography sx={classes.optionTitle}>
                      {M.get('welcome.wineImportModal.aiEnhancements.options.imageOnly.title')}
                    </Typography>
                    <Typography sx={classes.optionDescription}>
                      {M.get('welcome.wineImportModal.aiEnhancements.options.imageOnly.description')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="full_profile"
                control={<Radio sx={{ py: 0 }} />}
                sx={classes.optionControl}
                label={
                  <Box>
                    <Typography sx={classes.optionTitle}>
                      {M.get('welcome.wineImportModal.aiEnhancements.options.fullProfile.title')}
                    </Typography>
                    <Typography sx={classes.optionDescription}>
                      {M.get('welcome.wineImportModal.aiEnhancements.options.fullProfile.description')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>

            <Typography sx={classes.aiConfirmation}>
              {M.get('welcome.wineImportModal.aiEnhancements.confirmationText')}
            </Typography>

            <Divider sx={classes.sectionDivider} />

            <Typography sx={classes.notesTitle}>
              {M.get('welcome.wineImportModal.notes.title')}{' '}
              <Typography component="span" sx={classes.notesOptional}>
                {M.get('welcome.wineImportModal.notes.optionalLabel')}
              </Typography>
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={notes}
              onChange={event => setNotes(event.target.value)}
              placeholder={M.get('welcome.wineImportModal.notes.placeholder')}
              sx={classes.notesInput}
            />
          </DialogContentText>
        )}
      </DialogContent>
      {uploadProgress && (
        <Box sx={{ px: 5, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Uploading file {uploadProgress.current} of {uploadProgress.total}: {uploadProgress.fileName}
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress.percent} sx={{ height: 8, borderRadius: 1 }} />
        </Box>
      )}
      {uploadError && (
        <Box sx={{ px: 5, py: 1 }}>
          <Typography color="error" variant="body2">
            {uploadError}
          </Typography>
        </Box>
      )}
      <DialogActions sx={{ paddingX: 5, paddingY: 2 }}>
        {showConfirmation ? (
          <Button onClick={handleDialogClose} variant="contained" sx={classes.button}>
            {M.get('welcome.wineImportModal.postSubmission.buttonText')}
          </Button>
        ) : (
          <>
            <Button onClick={handleDialogClose} sx={classes.button} disabled={isSubmitting}>
              {M.get('welcome.wineImportModal.leftBtnText')}
            </Button>

            <Button onClick={handleImportSubmit} variant="contained" sx={classes.button} disabled={isSubmitting}>
              {M.get('welcome.wineImportModal.rightBtnText')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

WineListImportModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  serviceProviderId: PropTypes.number,
};

export default WineListImportModal;
